import { type FC, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useField } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useAutocompleteProductsQuery } from "@/partner/entities/products";

interface ProductOption {
  value: string;
  label: string;
}

interface ProductsFieldProps {
  /** Имя поля формы (react-final-form), по умолчанию productIds */
  name?: string;
}

// Порт референсного ProductsField: мультиселект «какие продукты дают доступ
// к этому контенту» — в отличие от CollectionsField (грузит до 1000
// коллекций сразу), продуктов может быть много, поэтому поиск идёт через
// autocompleteProducts (input — текст поиска, value — уже выбранные id,
// чтобы их подписи не терялись вне текущей выдачи поиска).
export const ProductsField: FC<ProductsFieldProps> = ({
  name = "productIds",
}) => {
  const { t } = useTranslation();
  const { input } = useField<string[]>(name);
  const value: string[] = input.value || [];
  const [search, setSearch] = useState("");

  const { data, loading } = useAutocompleteProductsQuery({
    fetchPolicy: "cache-and-network",
    variables: { input: search || undefined, value },
  });

  // options (по текущему поиску) и value (уже выбранные, даже если не
  // попадают в текущую выдачу) мёржим и дедупим — тот же приём, что и в
  // общем AutocompleteField.InputQuery (options.concat(value)).
  const optionsById = new Map<string, ProductOption>();
  for (const option of data?.autocompleteProducts?.options ?? []) {
    if (option) optionsById.set(option.value, option);
  }
  for (const option of data?.autocompleteProducts?.value ?? []) {
    if (option) optionsById.set(option.value, option);
  }
  const options = Array.from(optionsById.values());

  return (
    <Autocomplete<ProductOption, true>
      multiple
      openOnFocus
      fullWidth
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      autoComplete
      autoHighlight
      disableCloseOnSelect
      filterSelectedOptions
      // Фильтрация — на бэкенде (autocompleteProducts.input), локально по
      // подстроке не фильтруем, иначе только что введённый поиск моментально
      // "съедает" ещё не подгруженные варианты.
      filterOptions={(opts) => opts}
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      value={options.filter((option) => value.includes(option.value))}
      onInputChange={(_event, newInputValue, reason) => {
        setSearch(reason === "reset" ? "" : newInputValue);
      }}
      onChange={(_event, newValue) => {
        input.onChange(newValue.map((option) => option.value));
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={t("products.field.label")}
        />
      )}
    />
  );
};
